/**
 * Order Service
 *
 * Service layer for order operations.
 * Handles business logic, entity mapping, payload preparation, and error handling.
 *
 * Flow: API Layer -> Service Layer -> Component
 */

import { orderApi, getApiErrorMessage, getApiErrorStatusCode } from '../api/order-api';
import {
  CreateOrderPayload,
  CreateOrderItemPayload,
  CreateOrderAddressPayload,
  CreateOrderCustomerInfoPayload,
  CreateOrderSuccessData,
} from '../api/types/order-types';
import { Order, OrderItem, OrderStatus } from '../models/order/orders';
import { User } from '../models/user/user';
import { Address } from '../models/user/address';
import { PaymentMethod } from '../models/payment/payment';

// ============================================================================
// Service Types
// ============================================================================

/**
 * Input for creating an order from the checkout form
 */
export interface CreateOrderInput {
  items: OrderItem[];
  shippingCityId: number;
  address: Address;
  customerInfo: User;
  paymentMethod: PaymentMethod;
  subtotal: number;
  shipping: number;
  tax: number;
  total: number;
}

/**
 * Result of a successful order creation
 */
export interface CreateOrderResult {
  success: true;
  order: Order;
}

/**
 * Result of a failed order creation
 */
export interface CreateOrderError {
  success: false;
  error: string;
  errorCode?: OrderErrorCode;
  statusCode?: number;
}

/**
 * Union type for order creation result
 */
export type CreateOrderServiceResult = CreateOrderResult | CreateOrderError;

/**
 * Error codes for specific order errors
 */
export enum OrderErrorCode {
  INVALID_PRODUCT = 'INVALID_PRODUCT',
  INVALID_SHIPPING_CITY = 'INVALID_SHIPPING_CITY',
  INVALID_PAYLOAD = 'INVALID_PAYLOAD',
  NETWORK_ERROR = 'NETWORK_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

// ============================================================================
// Mapping Functions
// ============================================================================

/**
 * Map order items to API payload format
 */
function mapItemsToPayload(items: OrderItem[]): CreateOrderItemPayload[] {
  return items.map((item) => ({
    productId: item.product.id,
    quantity: item.quantity,
  }));
}

/**
 * Map address to API payload format
 */
function mapAddressToPayload(address: Address): CreateOrderAddressPayload {
  return {
    street: address.street,
    city: address.city,
    state: address.state,
    postalCode: address.postalCode || '',
    country: address.country,
  };
}

/**
 * Map customer info to API payload format
 */
function mapCustomerInfoToPayload(user: User): CreateOrderCustomerInfoPayload {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email || '',
    phone: user.phone,
    isGuest: user.isGuest,
  };
}

/**
 * Map payment method to API format
 */
function mapPaymentMethodToApi(paymentMethod: PaymentMethod): string {
  switch (paymentMethod) {
    case PaymentMethod.COD:
      return 'cod';
    case PaymentMethod.CREDIT_CARD:
      return 'credit_card';
    case PaymentMethod.PAYPAL:
      return 'paypal';
    case PaymentMethod.BANK_TRANSFER:
      return 'bank_transfer';
    default:
      return 'cod';
  }
}

/**
 * Create Order entity from input and API response
 */
function createOrderEntity(
  input: CreateOrderInput,
  apiResponse: CreateOrderSuccessData
): Order {
  return {
    id: apiResponse.orderId,
    orderNumber: apiResponse.orderNumber,
    items: input.items,
    customerInfo: input.customerInfo,
    shippingAddress: input.address,
    billingAddress: input.address,
    paymentMethod: input.paymentMethod,
    subtotal: input.subtotal,
    shipping: input.shipping,
    tax: input.tax,
    total: input.total,
    status: OrderStatus.CONFIRMED,
    createdAt: new Date().toISOString(),
    estimatedDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
  };
}

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Determine error code from error message
 */
function getErrorCode(message: string, statusCode?: number): OrderErrorCode {
  if (message.includes('Invalid product ID')) {
    return OrderErrorCode.INVALID_PRODUCT;
  }
  if (message.includes('Invalid shipping city ID')) {
    return OrderErrorCode.INVALID_SHIPPING_CITY;
  }
  if (message.includes('Invalid JSON')) {
    return OrderErrorCode.INVALID_PAYLOAD;
  }
  if (message.includes('Network error') || message.includes('timeout')) {
    return OrderErrorCode.NETWORK_ERROR;
  }
  if (statusCode === 500) {
    return OrderErrorCode.SERVER_ERROR;
  }
  return OrderErrorCode.UNKNOWN_ERROR;
}

// ============================================================================
// Service Class
// ============================================================================

/**
 * Order Service
 *
 * Provides methods for order operations with proper error handling
 * and entity mapping.
 */
class OrderService {
  /**
   * Create a new order
   *
   * @param input - Order creation input from checkout
   * @returns Promise resolving to order creation result
   */
  async createOrder(input: CreateOrderInput): Promise<CreateOrderServiceResult> {
    try {
      // Prepare the API payload
      const payload: CreateOrderPayload = {
        items: mapItemsToPayload(input.items),
        shippingCityId: input.shippingCityId,
        address: mapAddressToPayload(input.address),
        customerInfo: mapCustomerInfoToPayload(input.customerInfo),
        paymentMethod: mapPaymentMethodToApi(input.paymentMethod),
      };

      // Call the API
      const response = await orderApi.createOrder(payload);

      // Map response to Order entity
      const order = createOrderEntity(input, response.details.data);

      return {
        success: true,
        order,
      };
    } catch (error) {
      // Handle and format errors
      const errorMessage = getApiErrorMessage(error);
      const statusCode = getApiErrorStatusCode(error);
      const errorCode = getErrorCode(errorMessage, statusCode);

      return {
        success: false,
        error: errorMessage,
        errorCode,
        statusCode,
      };
    }
  }

  /**
   * Get user-friendly error message based on error code
   *
   * @param errorCode - The error code
   * @param fallbackMessage - Fallback message to use
   * @returns User-friendly error message
   */
  getErrorMessage(errorCode: OrderErrorCode, fallbackMessage: string): string {
    switch (errorCode) {
      case OrderErrorCode.INVALID_PRODUCT:
        return 'One or more products in your cart are no longer available.';
      case OrderErrorCode.INVALID_SHIPPING_CITY:
        return 'The selected shipping location is not available.';
      case OrderErrorCode.INVALID_PAYLOAD:
        return 'There was an error with your order. Please try again.';
      case OrderErrorCode.NETWORK_ERROR:
        return 'Unable to connect. Please check your internet connection.';
      case OrderErrorCode.SERVER_ERROR:
        return 'Our service is temporarily unavailable. Please try again later.';
      default:
        return fallbackMessage;
    }
  }
}

/**
 * Singleton instance of OrderService
 */
export const orderService = new OrderService();

export default orderService;
